import { toUrlKey } from "./collectionSearchUtils";
import {
  generateFilterHash,
  loadTimestampedMap,
  updateTimestampedMap,
} from "./filterStateManager";

/** Session flag for list-search carry-over dismissals (user cleared carried filters). */
const STORAGE_KEY = "collection_list_search_carry_dismissed";

export function getListFilterCarryEntryKey(
  domain: string,
  scopeId: string,
  collectionId: string,
  listSearch: string
): string {
  const hash = generateFilterHash({ search: [listSearch] });
  return `${domain}:${scopeId}:${collectionId}:${hash}`;
}

export function isListFilterCarryDismissed(entryKey: string): boolean {
  return entryKey in loadTimestampedMap(STORAGE_KEY);
}

export function markListFilterCarryDismissed(entryKey: string): void {
  updateTimestampedMap(STORAGE_KEY, (entries) => {
    entries[entryKey] = Date.now();
  });
}

/** Remove dismiss entries for a product/entity id (domain-scoped). Optional hygiene. */
export function clearListFilterCarryDismissals(
  domain: string,
  scopeId: string
): void {
  const prefix = `${domain}:${scopeId}:`;
  updateTimestampedMap(STORAGE_KEY, (entries) => {
    Object.keys(entries).forEach((key) => {
      if (key.startsWith(prefix)) delete entries[key];
    });
  });
}

/**
 * Expand a filter/attribute key into match variants so attribute `name` and
 * `display_name` still resolve when they differ by spacing/punctuation
 * (e.g. "Single Select" ↔ "SingleSelect" ↔ "single_select").
 */
export function expandFilterKeyVariants(key: string): string[] {
  const variants = new Set<string>();

  const add = (raw: string) => {
    const normalized = raw
      .replace(/\+/g, " ")
      .replace(/[\u00a0\u2000-\u200b]/g, " ")
      .trim()
      .toLowerCase();
    if (!normalized) return;
    variants.add(normalized);
    variants.add(toUrlKey(normalized));
    const compact = normalized.replace(/[^a-z0-9]+/g, "");
    if (compact) variants.add(compact);
  };

  add(key);
  try {
    add(decodeURIComponent(key));
  } catch {
    // ignore malformed URI sequences
  }
  try {
    add(decodeURIComponent(key.replace(/\+/g, "%20")));
  } catch {
    // ignore malformed URI sequences
  }

  return [...variants];
}

export type CarryOverColumn = {
  id: string;
  name: string;
  display_name?: string | null;
  is_filter_eligible?: boolean;
  /**
   * Mirrors the BE's `AttributeType.new_object_type` (server/src/agilis/models/attribute.py):
   * the fixed `object_type` for system attributes, or `slugify(name)` for
   * custom ones. This is the exact value `StorefrontFilterSettings.filter_type`
   * was seeded from when the products-list Portfolio Control was created
   * (server/src/agilis/views/storefront.py, `filter_type=attribute.new_object_type`)
   * — that column is written once at creation and never resynced. Matching
   * on it is more reliable than re-deriving a slug from `name`/`display_name`
   * client-side, since it reproduces the BE's own slugify output exactly
   * rather than approximating it. It only stops matching once the attribute
   * has been renamed *after* the Portfolio Control was created — no
   * client-side property can recover that case (see useListFilterCarryOver.ts).
   */
  new_object_type?: string;
};

/**
 * Map list-filter keys → collection column ids.
 * Precedence: exact `new_object_type` match (most authoritative — see
 * `CarryOverColumn.new_object_type`) > exact `name` match > normalized
 * variants of name/display_name/new_object_type. On normalized collisions,
 * keep the first column encountered (stable iteration order).
 */
export function buildFilterKeyToColumnIdMap(
  columns: CarryOverColumn[],
  translateDisplayName?: (displayName: string) => string
): Record<string, string> {
  const exactNameToId: Record<string, string> = {};
  const exactNewObjectTypeToId: Record<string, string> = {};
  const variantToId: Record<string, string> = {};

  columns.forEach((col) => {
    if (!col.is_filter_eligible) return;

    const exactName = col.name.trim().toLowerCase();
    if (exactName && !exactNameToId[exactName]) {
      exactNameToId[exactName] = col.id;
    }

    const exactNewObjectType = col.new_object_type?.trim().toLowerCase();
    if (exactNewObjectType && !exactNewObjectTypeToId[exactNewObjectType]) {
      exactNewObjectTypeToId[exactNewObjectType] = col.id;
    }

    const keys = [col.name, col.display_name, col.new_object_type].filter(
      Boolean
    ) as string[];
    if (col.display_name && translateDisplayName) {
      const translated = translateDisplayName(col.display_name);
      if (translated) keys.push(translated);
    }
    keys.forEach((key) => {
      expandFilterKeyVariants(key).forEach((variant) => {
        if (!variantToId[variant]) {
          variantToId[variant] = col.id;
        }
      });
    });
  });

  // Exact new_object_type > exact name > normalized variants.
  return { ...variantToId, ...exactNameToId, ...exactNewObjectTypeToId };
}

export function resolveColumnIdForFilterKey(
  filterKeyToId: Record<string, string>,
  filterKey: string
): string | undefined {
  const exact = filterKey.trim().toLowerCase();
  if (filterKeyToId[exact]) return filterKeyToId[exact];
  for (const variant of expandFilterKeyVariants(filterKey)) {
    const colId = filterKeyToId[variant];
    if (colId) return colId;
  }
  return undefined;
}
